/**
 * API client for the Inventory Audit Data Table (Phase 4).
 *
 * Wraps the /audit/* endpoints with typed fetchers. Each function handles
 * auth, serialization, and error extraction consistently with other API
 * modules in this directory.
 */
import { API } from "../config/api"

// --- Types matching backend Pydantic schemas ---

export interface AuditRunSummary {
  id: number
  run_type: string
  status: string
  started_at: string
  completed_at: string | null
  discrepancies_count: number | null
  pending_count: number | null
  completed_count: number | null
  unclassified_count: number | null
  dealer_duplicates_count: number | null
  // Website Audit counts (null on inventory runs)
  matched_count: number | null
  missing_count: number | null
  should_remove_count: number | null
}

export interface AuditRunListResponse {
  runs: AuditRunSummary[]
  total: number
}

export interface VehicleResponse {
  id: number
  audit_run_id: number
  sheet: string
  vin: string | null
  stock: string | null
  rooftop: string | null
  vehicle_type: string | null
  age_days: number | null
  /** HomeNet's photo count when the run was saved; null on older runs. */
  photos_count: number | null
  year: number | null
  make: string | null
  model: string | null
  trim: string | null
  tekion_status: string | null
  rr_step: string | null
  homenet_state: string | null
  disposition: string | null
  audit_diagnostic: string | null
  flag_type: string | null
  audit_sheet: string | null
  audit_sheet_row: number | null
  tekion_rooftop: string | null
  rr_source_file: string | null
  rr_recon_step: string | null
  hn_rooftop: string | null
  // Audit-remodel capture fields (null on runs saved before the remodel)
  problem_type: string | null
  problem_detail: string | null
  /** The vendor date behind problem_detail — a gone car's departure date, as
   * ISO yyyy-mm-dd. The server sends the date and never an age, so the label
   * ("Sold 43 days ago") is composed here and stays true as days pass. */
  problem_date: string | null
  website_problem_type: string | null
  /** The site's own listing label (live / in transit / …) on should-remove
   * rows — what the SITE says about the car. */
  site_label: string | null
  /** Live-listing link — stored on website rows, borrowed by VIN for
   * inventory rows (Completed, search results) from the chained run. */
  website_url: string | null
  tk_bucket: string | null
  rr_bucket: string | null
  hn_bucket: string | null
  // Derived server-side from rr_recon_step (Mill board is display-only)
  mill_bucket: string | null
  /** When INVI first flagged this (car, problem); null when untracked. A critical
   *  row shows its days open from this instead of a NEW pill. */
  first_seen_at: string | null
}

export interface ProblemTypeInfo {
  slug: string
  label: string
  meaning: string
  action: string
  severity: string
  /** INVI's own problems — shown on the page, never offered on a dealer's alert email. */
  internal: boolean
}

export interface ProblemTypesResponse {
  problem_types: ProblemTypeInfo[]
}

export interface VehicleListResponse {
  vehicles: VehicleResponse[]
  total_matching: number
  total_unfiltered: number
  page: number
  size: number
  sheet: string
}

export interface AgeRange {
  min: number
  max: number
}

export interface FilterOptionsResponse {
  rooftops: string[]
  vehicle_types: string[]
  tekion_statuses: string[]
  flag_types: string[]
  age_range: AgeRange
}

export interface SheetListResponse {
  sheets: string[]
}

export interface AuditColumnConfig {
  field: string
  label: string
  width: number
  min_width: number | null
  max_width: number | null
  pinned: boolean
  center_aligned: boolean
  sortable: boolean
}

export interface AuditColumnsResponse {
  sheet: string
  columns: AuditColumnConfig[]
}

export interface ListVehiclesParams {
  sheet: string
  page?: number
  size?: number
  sort?: string
  rooftop?: string
  vehicle_type?: string
  tekion_status?: string
  flag_type?: string
  age_min?: number
  age_max?: number
  search?: string
}

// --- Auth helper (local copy matching existing pattern across api files) ---

const TOKEN_KEY = "invi_token"

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY)
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = `Request failed: ${res.status}`
    try {
      const body = await res.json()
      if (body.detail) detail = body.detail
    } catch {
      // fall through with generic message
    }
    throw new Error(detail)
  }
  return res.json()
}

// --- Fetchers ---

export async function getProblemTypes(): Promise<ProblemTypesResponse> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.auditProblemTypes}`, {
    headers: authHeaders(),
  })
  return handleResponse<ProblemTypesResponse>(res)
}

export interface StatusLegendEntry {
  value: string
  bucket: string
}

export interface StatusLegendFeed {
  key: string
  label: string
  entries: StatusLegendEntry[]
}

export interface StatusLegendResponse {
  feeds: StatusLegendFeed[]
}

// The classifier's own raw-value → bucket maps, served so the in-app legend
// can never drift from the engine.
export async function getStatusLegend(): Promise<StatusLegendResponse> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.auditStatusLegend}`, {
    headers: authHeaders(),
  })
  return handleResponse<StatusLegendResponse>(res)
}

export interface AuditRooftopOption {
  id: number
  slug: string
  name: string
  audit_label: string
}

export interface AuditRooftopsResponse {
  rooftops: AuditRooftopOption[]
}

/**
 * The stores the audit page's rooftop dropdown offers — only rooftops with an
 * inventory feed label, since a store with no feed can never match an audit
 * row. The store view is a convenience default, not a security boundary.
 */
export async function listAuditRooftops(
  dealerGroupId?: number | null,
): Promise<AuditRooftopsResponse> {
  const groupParam = dealerGroupId != null ? `?dealer_group_id=${dealerGroupId}` : ""
  const res = await fetch(`${API.baseUrl}${API.endpoints.auditRooftops}${groupParam}`, {
    headers: authHeaders(),
  })
  return handleResponse<AuditRooftopsResponse>(res)
}

export interface ProblemSummaryEntry {
  problem_type: string
  count: number
  /** Critical groups only: when INVI first flagged the group's oldest car. */
  oldest_first_seen_at?: string | null
}

export interface ProblemSummaryResponse {
  problems: ProblemSummaryEntry[]
  unlabeled_count: number
}

export interface ProblemVehiclesResponse {
  vehicles: VehicleResponse[]
  total: number
  page: number
  size: number
}

// `rooftopSlug` is the page-level store view — it narrows every count so the
// tiles agree with the store-scoped group lists beneath them.
export async function getProblemSummary(
  runId: number,
  rooftopSlug?: string | null,
): Promise<ProblemSummaryResponse> {
  const rooftopParam = rooftopSlug ? `?rooftop_slug=${encodeURIComponent(rooftopSlug)}` : ""
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}/${runId}/problem-summary${rooftopParam}`,
    { headers: authHeaders() }
  )
  return handleResponse<ProblemSummaryResponse>(res)
}

export interface ResolvedProblem {
  vin: string
  problem_type: string
  year: number | null
  make: string | null
  model: string | null
  stock: string | null
  rooftop: string | null
}

export interface RunDiffResponse {
  comparable: boolean
  previous_run_id: number | null
  previous_started_at: string | null
  new_count: number
  resolved_count: number
  new_vins_by_problem: Record<string, string[]>
  resolved: ResolvedProblem[]
}

export interface WorkedMarkEntry {
  vin: string
  problem_type: string
  marked_at: string
}

export interface WorkedMarksResponse {
  marks: WorkedMarkEntry[]
}

export interface ProblemHistoryEntry {
  run_id: number
  started_at: string
  /** Every flag the car carried in that audit — a chained inventory+website
   *  pair is one entry, so this can name findings from both halves. */
  problem_types: string[]
}

export interface ProblemHistoryResponse {
  history: ProblemHistoryEntry[]
}

export async function getRunDiff(
  runId: number,
  rooftopSlug?: string | null,
): Promise<RunDiffResponse> {
  const rooftopParam = rooftopSlug ? `?rooftop_slug=${encodeURIComponent(rooftopSlug)}` : ""
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}/${runId}/diff${rooftopParam}`,
    { headers: authHeaders() }
  )
  return handleResponse<RunDiffResponse>(res)
}

export async function listWorkedMarks(runId: number): Promise<WorkedMarksResponse> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}/${runId}/worked-marks`,
    { headers: authHeaders() }
  )
  return handleResponse<WorkedMarksResponse>(res)
}

async function sendWorkedMark(
  method: "PUT" | "DELETE",
  dealerGroupId: number,
  vin: string,
  problemType: string,
): Promise<void> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditWorkedMarks}?dealer_group_id=${dealerGroupId}`,
    {
      method,
      headers: authHeaders(),
      body: JSON.stringify({ vin, problem_type: problemType }),
    }
  )
  // 204 has no body — don't parse; surface backend detail on failure.
  if (!res.ok) {
    let detail = `Request failed: ${res.status}`
    try {
      const body = await res.json()
      if (body.detail) detail = body.detail
    } catch {
      // fall through with generic message
    }
    throw new Error(detail)
  }
}

export async function putWorkedMark(
  dealerGroupId: number, vin: string, problemType: string,
): Promise<void> {
  return sendWorkedMark("PUT", dealerGroupId, vin, problemType)
}

export async function removeWorkedMark(
  dealerGroupId: number, vin: string, problemType: string,
): Promise<void> {
  return sendWorkedMark("DELETE", dealerGroupId, vin, problemType)
}

export async function getProblemHistory(
  dealerGroupId: number, vin: string, problemType: string,
): Promise<ProblemHistoryResponse> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditProblemHistory}?dealer_group_id=${dealerGroupId}&vin=${encodeURIComponent(vin)}&problem_type=${encodeURIComponent(problemType)}`,
    { headers: authHeaders() }
  )
  return handleResponse<ProblemHistoryResponse>(res)
}

// The website run auto-chained from this inventory run — null for manual-era
// runs and failed chains. Feeds the folded-in website sheet tabs + download.
export async function getChainedWebsiteRun(
  runId: number,
): Promise<{ run: AuditRunSummary | null }> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}/${runId}/chained-website-run`,
    { headers: authHeaders() }
  )
  return handleResponse<{ run: AuditRunSummary | null }>(res)
}

// Worklist search: match VIN, stock, make, or model across every sheet of the
// run. Two-character minimum (the endpoint enforces it too).
export async function searchRunVehicles(
  runId: number,
  q: string,
  limit: number = 50,
  rooftopSlug?: string | null,
): Promise<ProblemVehiclesResponse> {
  const params = new URLSearchParams({ q, limit: String(limit) })
  // Keep search inside the page-level store view — without this a search from
  // the Dodge view would swap other stores' cars into the list.
  if (rooftopSlug) params.set("rooftop_slug", rooftopSlug)
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}/${runId}/search?${params.toString()}`,
    { headers: authHeaders() }
  )
  return handleResponse<ProblemVehiclesResponse>(res)
}

/**
 * The OTHER worklist groups a car appears in. Empty for a car flagged once,
 * which is 4,198 of 4,217 on run 86 — the exception is worth naming precisely
 * because it is rare.
 */
export async function getVehicleOtherGroups(
  runId: number,
  vehicleId: number,
): Promise<{ groups: string[] }> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}/${runId}/vehicles/${vehicleId}/other-groups`,
    { headers: authHeaders() }
  )
  return handleResponse<{ groups: string[] }>(res)
}

export interface VehicleNoteEntry {
  id: number
  vin: string
  problem_type: string | null
  body: string
  author_name: string | null
  created_at: string
  /** Stock the car carried when written (backend stamps it for the stint
   * rule). Mirrors the Pydantic schema — keep in lockstep with the copy in
   * api/photoList.ts (known duplication, tracked cleanup). */
  stock: string | null
}

/**
 * Notes are keyed by VIN, not by run — they outlive the run they were written
 * during, which is the point of them.
 */
export async function listVehicleNotes(
  vin: string,
  dealerGroupId: number,
): Promise<{ notes: VehicleNoteEntry[] }> {
  const res = await fetch(
    `${API.baseUrl}/audit/vehicles/${encodeURIComponent(vin)}/notes?dealer_group_id=${dealerGroupId}`,
    { headers: authHeaders() }
  )
  return handleResponse<{ notes: VehicleNoteEntry[] }>(res)
}

export async function addVehicleNote(
  vin: string,
  dealerGroupId: number,
  body: string,
  problemType: string | null,
): Promise<VehicleNoteEntry> {
  const res = await fetch(
    `${API.baseUrl}/audit/vehicles/${encodeURIComponent(vin)}/notes?dealer_group_id=${dealerGroupId}`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ body, problem_type: problemType }),
    }
  )
  return handleResponse<VehicleNoteEntry>(res)
}

export interface GroupFilterOptionsResponse {
  rooftops: string[]
  vehicle_types: string[]
  age_range: { min: number; max: number }
}

/** The rooftops, stock types and age bounds present in ONE worklist group —
 *  scoped to the page-level store view when one is active, so a dropdown
 *  never offers a value the narrowed group does not contain. */
export async function getGroupFilterOptions(
  runId: number,
  problemType: string,
  rooftopSlug?: string | null,
): Promise<GroupFilterOptionsResponse> {
  const rooftopParam = rooftopSlug ? `?rooftop_slug=${encodeURIComponent(rooftopSlug)}` : ""
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}/${runId}/problems/${encodeURIComponent(problemType)}/filter-options${rooftopParam}`,
    { headers: authHeaders() }
  )
  return handleResponse<GroupFilterOptionsResponse>(res)
}

export async function listProblemVehicles(
  runId: number,
  problemType: string,
  page: number = 1,
  size: number = 200,
  sort?: { field: string; direction: "asc" | "desc" } | null,
  filters?: {
    rooftops?: string[]
    vehicleTypes?: string[]
    ageMin?: number | null
    ageMax?: number | null
  } | null,
  rooftopSlug?: string | null,
): Promise<ProblemVehiclesResponse> {
  // Sorting is applied server-side so it orders the whole group rather than the
  // page in hand — Completed runs past 1,600 rows against a page size of 200.
  const params = new URLSearchParams({ page: String(page), size: String(size) })
  // The page-level store view — a different axis from the per-group rooftop
  // filter below: it applies to every group, duplicates included (whole
  // families), and the backend resolves the slug to its audit label.
  if (rooftopSlug) params.set("rooftop_slug", rooftopSlug)
  if (sort) {
    params.set("sort_field", sort.field)
    params.set("sort_direction", sort.direction)
  }
  // Repeated keys, matching the sheet view's list filters — FastAPI reads
  // ?rooftop=A&rooftop=B as a list.
  filters?.rooftops?.forEach((v) => params.append("rooftop", v))
  filters?.vehicleTypes?.forEach((v) => params.append("vehicle_type", v))
  if (filters?.ageMin != null) params.set("age_min", String(filters.ageMin))
  if (filters?.ageMax != null) params.set("age_max", String(filters.ageMax))
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}/${runId}/problems/${encodeURIComponent(problemType)}/vehicles?${params}`,
    { headers: authHeaders() }
  )
  return handleResponse<ProblemVehiclesResponse>(res)
}

export async function listAuditRuns(
  limit: number = 30,
  dealerGroupId?: number | null,
): Promise<AuditRunListResponse> {
  const groupParam = dealerGroupId != null ? `&dealer_group_id=${dealerGroupId}` : ""
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}?limit=${limit}${groupParam}`,
    { headers: authHeaders() }
  )
  return handleResponse<AuditRunListResponse>(res)
}

export async function getAuditRun(
  runId: number
): Promise<AuditRunSummary> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}/${runId}`,
    { headers: authHeaders() }
  )
  return handleResponse<AuditRunSummary>(res)
}

export async function getAuditSheets(
  runId: number
): Promise<SheetListResponse> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}/${runId}/sheets`,
    { headers: authHeaders() }
  )
  return handleResponse<SheetListResponse>(res)
}

export async function listVehicles(
  runId: number,
  params: ListVehiclesParams
): Promise<VehicleListResponse> {
  const query = new URLSearchParams()
  query.append("sheet", params.sheet)
  if (params.page !== undefined) query.append("page", String(params.page))
  if (params.size !== undefined) query.append("size", String(params.size))
  if (params.sort) query.append("sort", params.sort)
  if (params.rooftop) query.append("rooftop", params.rooftop)
  if (params.vehicle_type) query.append("vehicle_type", params.vehicle_type)
  if (params.tekion_status) query.append("tekion_status", params.tekion_status)
  if (params.flag_type) query.append("flag_type", params.flag_type)
  if (params.age_min !== undefined) query.append("age_min", String(params.age_min))
  if (params.age_max !== undefined) query.append("age_max", String(params.age_max))
  if (params.search) query.append("search", params.search)

  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}/${runId}/vehicles?${query.toString()}`,
    { headers: authHeaders() }
  )
  return handleResponse<VehicleListResponse>(res)
}

export async function getVehicleDetail(
  runId: number,
  vehicleId: number
): Promise<VehicleResponse> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}/${runId}/vehicles/${vehicleId}`,
    { headers: authHeaders() }
  )
  return handleResponse<VehicleResponse>(res)
}

export async function getFilterOptions(
  runId: number,
  sheet: string
): Promise<FilterOptionsResponse> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}/${runId}/filter-options?sheet=${encodeURIComponent(sheet)}`,
    { headers: authHeaders() }
  )
  return handleResponse<FilterOptionsResponse>(res)
}

export async function getColumnConfig(
  sheet: string
): Promise<AuditColumnsResponse> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditColumns}/${encodeURIComponent(sheet)}`,
    { headers: authHeaders() }
  )
  return handleResponse<AuditColumnsResponse>(res)
}

export async function exportVehicles(
  runId: number,
  params: Omit<ListVehiclesParams, "page" | "size">
): Promise<Blob> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRuns}/${runId}/export`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(params),
    }
  )
  if (!res.ok) {
    let detail = `Export failed: ${res.status}`
    try {
      const body = await res.json()
      if (body.detail) detail = body.detail
    } catch {
      // fall through
    }
    throw new Error(detail)
  }
  return res.blob()
}

// --- Report builder: per-group vendor-file upload + audit generation ---

export interface AuditUploadedFile {
  filename: string
  vendor: string
  size_kb: number
  modified: string
}

export interface AuditUploadResult {
  filename: string
  vendor: string
  size: number
}

export interface AuditFileSlot {
  present: boolean
  filename: string | null
  modified: string | null
}

export interface AuditRapidReconSlot extends AuditFileSlot {
  key: string
  label: string
}

export interface AuditFileStatus {
  can_run: boolean
  tekion: AuditFileSlot
  homenet: AuditFileSlot
  rapid_recon: AuditRapidReconSlot[]
}

export interface InventoryAuditRunResult {
  status: string
  discrepancies: number
  pending: number
  completed: number
  unclassified: number
  dealer_duplicates: number
  audit_run_id: number | null
  // Phase 6 auto-chained website comparison — "failed" never sinks the
  // inventory result; website_error carries the reason.
  website_status: "success" | "failed"
  website_matched: number | null
  website_missing: number | null
  website_should_remove: number | null
  website_audit_run_id: number | null
  website_error: string | null
}

// Mirrors schemas.AuditBuilderJobResponse. POST /audit/run returns immediately
// with one of these and the pipeline runs server-side — holding the request
// open instead meant the proxy cut the connection at ~2 minutes and a finished
// audit looked failed in the browser. `result` is set once status is
// "success", `error` once "failed"; while "running" the client polls.
export interface AuditRunJob {
  id: number
  dealer_group_id: number
  status: "running" | "success" | "failed"
  error: string | null
  result: InventoryAuditRunResult | null
  started_at: string
  completed_at: string | null
}

// --- Upload: direct-to-R2 with proxy fallback ---

export interface AuditUploadPresign {
  mode: "direct" | "proxy"
  upload_url: string | null
  key: string | null
  content_type: string | null
}

/** Fraction of the file's bytes sent so far, 0..1. */
export type UploadProgressHandler = (fraction: number) => void

/** Error name set when the stall watchdog aborts a transfer, so callers can
 *  phrase the failure as "stalled" rather than "rejected". */
export const UPLOAD_STALLED = "UploadStalled"

// Progress events fire continuously while any byte moves, so a full minute of
// silence means the transfer is genuinely dead — abort instead of spinning
// forever (the page then asks the server whether the file landed anyway).
const UPLOAD_STALL_MS = 60_000
// After the last byte the server still has work to do before replying (the
// proxy path re-uploads the whole file to storage). Allow it longer.
const UPLOAD_RESPONSE_TIMEOUT_MS = 180_000

/**
 * Send an already-configured XMLHttpRequest, reporting upload progress and
 * aborting if the transfer stalls. XMLHttpRequest instead of fetch because
 * fetch cannot report upload progress. Resolves with the raw status/body —
 * callers interpret per endpoint (R2 PUT vs. backend JSON).
 */
function sendWithProgress(
  xhr: XMLHttpRequest,
  body: File | FormData,
  onProgress?: UploadProgressHandler,
): Promise<{ status: number; responseText: string }> {
  return new Promise((resolve, reject) => {
    let watchdog: ReturnType<typeof setTimeout> | undefined
    let stalled = false
    const arm = (ms: number) => {
      clearTimeout(watchdog)
      watchdog = setTimeout(() => {
        stalled = true
        xhr.abort()
      }, ms)
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress?.(e.loaded / e.total)
      arm(e.lengthComputable && e.loaded >= e.total ? UPLOAD_RESPONSE_TIMEOUT_MS : UPLOAD_STALL_MS)
    }
    xhr.onload = () => {
      clearTimeout(watchdog)
      resolve({ status: xhr.status, responseText: xhr.responseText })
    }
    xhr.onerror = () => {
      clearTimeout(watchdog)
      reject(new Error("The connection dropped during the upload."))
    }
    xhr.onabort = () => {
      clearTimeout(watchdog)
      const error = new Error("The upload stalled — no data moved for a while.")
      if (stalled) error.name = UPLOAD_STALLED
      reject(error)
    }
    arm(UPLOAD_STALL_MS)
    xhr.send(body)
  })
}

async function presignAuditUpload(
  filename: string,
  dealerGroupId: number,
): Promise<AuditUploadPresign> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditUploadPresign}?dealer_group_id=${dealerGroupId}`,
    { method: "POST", headers: authHeaders(), body: JSON.stringify({ filename }) },
  )
  return handleResponse<AuditUploadPresign>(res)
}

async function confirmAuditUpload(
  key: string,
  filename: string,
  dealerGroupId: number,
): Promise<AuditUploadResult> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditUploadConfirm}?dealer_group_id=${dealerGroupId}`,
    { method: "POST", headers: authHeaders(), body: JSON.stringify({ key, filename }) },
  )
  return handleResponse<AuditUploadResult>(res)
}

async function uploadDirectToR2(
  file: File,
  presign: AuditUploadPresign,
  onProgress?: UploadProgressHandler,
): Promise<void> {
  const xhr = new XMLHttpRequest()
  xhr.open("PUT", presign.upload_url as string)
  // Content-Type is signed into the presigned URL — a mismatch fails the PUT.
  xhr.setRequestHeader("Content-Type", presign.content_type ?? "application/octet-stream")
  const { status, responseText } = await sendWithProgress(xhr, file, onProgress)
  if (status < 200 || status >= 300) {
    throw new Error(`Storage upload failed: ${responseText || `HTTP ${status}`}`)
  }
}

// The classic multipart path, kept for local dev (no R2 there). FormData sets
// its own multipart boundary, so only the Authorization header is sent.
async function uploadViaBackend(
  file: File,
  dealerGroupId: number,
  onProgress?: UploadProgressHandler,
): Promise<AuditUploadResult> {
  const formData = new FormData()
  formData.append("file", file)
  const token = localStorage.getItem(TOKEN_KEY)
  const xhr = new XMLHttpRequest()
  xhr.open("POST", `${API.baseUrl}${API.endpoints.auditUpload}?dealer_group_id=${dealerGroupId}`)
  if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`)
  const { status, responseText } = await sendWithProgress(xhr, formData, onProgress)
  if (status < 200 || status >= 300) {
    let detail = `Request failed: ${status}`
    try {
      const body = JSON.parse(responseText)
      if (body.detail) detail = body.detail
    } catch {
      // fall through with generic message
    }
    throw new Error(detail)
  }
  return JSON.parse(responseText) as AuditUploadResult
}

/**
 * Upload a Tekion / RapidRecon / HomeNet file into the active group's audit
 * inputs. Prefers the direct-to-R2 path (presign → browser PUT → confirm) so
 * the ~85 MB Tekion export travels once, straight to storage, instead of
 * through the backend twice; local dev has no R2 and answers mode="proxy",
 * falling back to the classic multipart endpoint. Either way onProgress
 * reports the fraction of bytes sent, and a stalled transfer is aborted
 * rather than left spinning.
 */
export async function uploadAuditFile(
  file: File,
  dealerGroupId: number,
  onProgress?: UploadProgressHandler,
): Promise<AuditUploadResult> {
  const presign = await presignAuditUpload(file.name, dealerGroupId)
  if (presign.mode === "direct" && presign.upload_url && presign.key) {
    await uploadDirectToR2(file, presign, onProgress)
    return confirmAuditUpload(presign.key, file.name, dealerGroupId)
  }
  return uploadViaBackend(file, dealerGroupId, onProgress)
}

export async function listAuditFiles(
  dealerGroupId: number,
): Promise<AuditUploadedFile[]> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditUploadedFiles}?dealer_group_id=${dealerGroupId}`,
    { headers: authHeaders() },
  )
  return handleResponse<AuditUploadedFile[]>(res)
}

export async function deleteAuditFile(
  filename: string,
  dealerGroupId: number,
): Promise<void> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditUploadedFiles}/${encodeURIComponent(filename)}?dealer_group_id=${dealerGroupId}`,
    { method: "DELETE", headers: authHeaders() },
  )
  await handleResponse<{ deleted: string }>(res)
}

export async function fetchAuditFileStatus(
  dealerGroupId: number,
): Promise<AuditFileStatus> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditFileStatus}?dealer_group_id=${dealerGroupId}`,
    { headers: authHeaders() },
  )
  return handleResponse<AuditFileStatus>(res)
}

export async function runInventoryAudit(
  dealerGroupId: number,
): Promise<AuditRunJob> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRun}?dealer_group_id=${dealerGroupId}`,
    { method: "POST", headers: authHeaders() },
  )
  return handleResponse<AuditRunJob>(res)
}

export async function fetchAuditRunJob(jobId: number): Promise<AuditRunJob> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.auditRunJob(jobId)}`,
    { headers: authHeaders() },
  )
  return handleResponse<AuditRunJob>(res)
}

const RUN_JOB_POLL_INTERVAL_MS = 4000
// Well past any plausible run (~5 min today) so a genuine hang eventually
// surfaces instead of spinning forever.
const RUN_JOB_TIMEOUT_MS = 20 * 60 * 1000
// A dropped poll must not fail the run — transient network blips during a
// multi-minute wait are the exact problem the job flow exists to survive.
// Only a sustained outage (this many misses in a row) gives up.
const RUN_JOB_MAX_CONSECUTIVE_POLL_FAILURES = 5

/**
 * Poll an audit run job until it leaves "running". Resolves with the terminal
 * job (status "success" or "failed" — the caller reads result/error); rejects
 * only when the server is unreachable for several polls in a row or the
 * overall timeout passes, both with refresh-and-check guidance since the run
 * itself may well have finished.
 */
export async function waitForAuditRunJob(jobId: number): Promise<AuditRunJob> {
  const startedAt = Date.now()
  let consecutiveFailures = 0
  while (Date.now() - startedAt < RUN_JOB_TIMEOUT_MS) {
    await new Promise((resolve) => setTimeout(resolve, RUN_JOB_POLL_INTERVAL_MS))
    try {
      const job = await fetchAuditRunJob(jobId)
      consecutiveFailures = 0
      if (job.status !== "running") return job
    } catch {
      consecutiveFailures += 1
      if (consecutiveFailures >= RUN_JOB_MAX_CONSECUTIVE_POLL_FAILURES) {
        throw new Error(
          "Lost contact with the server while the audit was running — " +
            "refresh the audit page to see whether it finished.",
        )
      }
    }
  }
  throw new Error(
    "The audit run is taking much longer than expected — refresh the audit " +
      "page to see whether it finished.",
  )
}

/**
 * The audit workbook, one sheet per flag — what the Download button produces.
 * Takes one optional filter: the page-level store view, so the download
 * matches the page the operator is looking at. Omitted = the whole run.
 */
export async function exportWorklistWorkbook(
  runId: number,
  rooftopSlug?: string | null,
): Promise<Blob> {
  const rooftopParam = rooftopSlug ? `?rooftop_slug=${encodeURIComponent(rooftopSlug)}` : ""
  const res = await fetch(
    `${API.baseUrl}/audit/runs/${runId}/export-worklist${rooftopParam}`,
    { headers: authHeaders() },
  );
  if (!res.ok) {
    let detail = `Download failed: ${res.status}`;
    try {
      const body = await res.json();
      if (body.detail) detail = body.detail;
    } catch {
      // fall through with the generic message
    }
    throw new Error(detail);
  }
  return res.blob();
}

export async function exportFullAudit(runId: number): Promise<Blob> {
  const res = await fetch(`${API.baseUrl}/audit/runs/${runId}/export-full`, {
    method: "POST",
    headers: {
      ...authHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    let detail = `Full export failed: ${res.status}`;
    try {
      const body = await res.json();
      if (body.detail) detail = body.detail;
    } catch {
      // fall through
    }
    throw new Error(detail);
  }
  return res.blob();
}
